import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAdminApi } from '../../hooks/useAdminApi';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Users,
  Key,
  CreditCard,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  Building2,
  IndianRupee
} from 'lucide-react';

const AdminDashboard = () => {
  const { get, loading } = useAdminApi();
  const [stats, setStats] = useState({
    tenants: { total: 0, active: 0, pending: 0 },
    licenses: { total: 0, active: 0, expiringSoon: 0, expired: 0 },
    payments: { total: 0, thisMonth: 0, pending: 0 },
    revenue: { total: 0, thisMonth: 0 }
  });
  const [recentTenants, setRecentTenants] = useState([]);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      // Fetch dashboard stats
      const statsRes = await get('/dashboard/stats');
      if (statsRes.success) {
        setStats(statsRes.stats);
      }

      // Fetch recent tenants
      const tenantsRes = await get('/tenants?limit=5&sort=-createdAt');
      if (tenantsRes.success) {
        setRecentTenants(tenantsRes.data || tenantsRes.tenants || []);
      }

      // Fetch expiring licenses
      const licensesRes = await get('/licenses?status=expiring_soon&limit=5');
      if (licensesRes.success) {
        setExpiringLicenses(licensesRes.data || licensesRes.licenses || []);
      }

      // Fetch recent payments
      const paymentsRes = await get('/payments?limit=5&sort=-createdAt');
      if (paymentsRes.success) {
        setRecentPayments(paymentsRes.data || paymentsRes.payments || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const StatCard = ({ title, value, subValue, icon: Icon, color, link }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {subValue && (
            <p className="text-sm text-gray-500 mt-1">{subValue}</p>
          )}
        </div>
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
      </div>
      {link && (
        <Link
          to={link}
          className="mt-4 inline-flex items-center text-sm text-purple-600 hover:text-purple-700 font-medium"
        >
          View all <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      )}
    </div>
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      suspended: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-500 mt-1">Welcome to Besto Admin Panel</p>
          </div>
          <button
            onClick={fetchDashboardData}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Tenants"
            value={stats.tenants.total}
            subValue={`${stats.tenants.active} active, ${stats.tenants.pending} pending`}
            icon={Users}
            color="bg-gradient-to-r from-blue-500 to-blue-600"
            link="/admin/tenants"
          />
          <StatCard
            title="Active Licenses"
            value={stats.licenses.active}
            subValue={`${stats.licenses.expiringSoon} expiring soon`}
            icon={Key}
            color="bg-gradient-to-r from-purple-500 to-purple-600"
            link="/admin/licenses"
          />
          <StatCard
            title="Payments"
            value={stats.payments.total}
            subValue={`${stats.payments.thisMonth} this month`}
            icon={CreditCard}
            color="bg-gradient-to-r from-green-500 to-green-600"
            link="/admin/payments"
          />
          <StatCard
            title="Revenue"
            value={formatCurrency(stats.revenue.total)}
            subValue={`${formatCurrency(stats.revenue.thisMonth)} this month`}
            icon={IndianRupee}
            color="bg-gradient-to-r from-pink-500 to-pink-600"
          />
        </div>

        {/* Alert Cards */}
        {(stats.licenses.expiringSoon > 0 || stats.licenses.expired > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stats.licenses.expiringSoon > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-yellow-800">Licenses Expiring Soon</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    {stats.licenses.expiringSoon} license(s) will expire within 30 days
                  </p>
                  <Link to="/admin/licenses?status=expiring_soon" className="text-sm text-yellow-800 font-medium hover:underline mt-2 inline-block">
                    View licenses →
                  </Link>
                </div>
              </div>
            )}
            {stats.licenses.expired > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-800">Expired Licenses</h3>
                  <p className="text-sm text-red-700 mt-1">
                    {stats.licenses.expired} license(s) have expired and need attention
                  </p>
                  <Link to="/admin/licenses?status=expired" className="text-sm text-red-800 font-medium hover:underline mt-2 inline-block">
                    View licenses →
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recent Activity Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Tenants */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Recent Tenants</h2>
              <Link to="/admin/tenants" className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                View all
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {recentTenants.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No tenants yet
                </div>
              ) : (
                recentTenants.map((tenant) => (
                  <div key={tenant._id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-gray-900">{tenant.companyName}</p>
                        <p className="text-sm text-gray-500">{tenant.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(tenant.provisioningStatus || tenant.subscription?.status || tenant.status)}
                      <p className="text-xs text-gray-400 mt-1">{formatDate(tenant.createdAt)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Expiring Licenses */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Expiring Licenses</h2>
              <Link to="/admin/licenses?status=expiring_soon" className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                View all
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {expiringLicenses.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  No licenses expiring soon
                </div>
              ) : (
                expiringLicenses.map((license) => (
                  <div key={license._id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-gray-900">{license.tenant?.companyName}</p>
                        <p className="text-sm text-gray-500">{license.plan} plan</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-yellow-600">
                        {license.daysRemaining} days left
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Expires {formatDate(license.expiresAt)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Recent Payments */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Recent Payments</h2>
            <Link to="/admin/payments" className="text-sm text-purple-600 hover:text-purple-700 font-medium">
              View all
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentPayments.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No payments yet
                    </td>
                  </tr>
                ) : (
                  recentPayments.map((payment) => (
                    <tr key={payment._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-white" />
                          </div>
                          <span className="ml-3 font-medium text-gray-900">
                            {payment.tenant?.companyName || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 text-gray-500 capitalize">
                        {payment.plan}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {formatDate(payment.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
